// Dependencies
import {
	$convertFromMarkdownString,
	$convertToMarkdownString,
	TEXT_FORMAT_TRANSFORMERS,
	TEXT_MATCH_TRANSFORMERS,
} from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import type { EditorState } from "lexical";
import React, { useEffect, useMemo, useState } from "react";

import Spacer from "../Spacer";
//components
import Text, { type BaseProps as TextProps } from "../Text";
import { emptyState, getEditorNodes } from "./helpers/getEditorNodes";
import { getEditorPlugins } from "./helpers/getEditorPlugins";
import { CharacterCounterPlugin } from "./plugins/CharacterCounterPlugin";
// Project Dependencies
import { theme } from "./theme/theme";
import css from "./theme/theme.module.scss";

export const DEFAULT_EDITOR_NAMESPACE = "lexical-editor";
// Until we need more transformers, let's be explicit about which we need.
// https://lexical.dev/docs/packages/lexical-markdown#transformers
const TRANSFORMERS = [...TEXT_FORMAT_TRANSFORMERS, ...TEXT_MATCH_TRANSFORMERS];

export type EditorFeature = "bold" | "italic" | "underline" | "strikethrough" | "heading" | "quote" | "link" | "list";

export type TextEditorProps = {
	/**
	 * the label of the text editor
	 */
	label?: string;
	/**
	 * The label's text props
	 */
	labelProps?: TextProps;
	/**
	 * The initial value of the editor (can be plain text or markdown).
	 * */
	initialValue?: string;
	/**
	 * A callback that is called whenever the editor's content changes.
	 * Provides the markdown content and plain text content.
	 * @default () => {}
	 * */
	onChange?: (markdownContent: string, plainTextContent: string) => void;
	/**
	 * Whether the editor should be a rich text editor or a plain text editor.
	 * Must be true if you want to use any of the rich text features outside of hashtags.
	 * Can trim down the editor's features by passing in an array for the `disableFeatures` prop.
	 * @default false
	 * */
	isRichText?: boolean;
	/**
	 * Should AutoFocus on the editor
	 */
	autoFocus?: boolean;
	/**
	 * An array of features to disable. If provided, the editor will not produce markdown for the
	 * disabled features and will not format the disabled features in the editor. Needs to be used
	 * in conjunction with the `isRichText` prop.
	 * @default []
	 * */
	disableFeatures?: EditorFeature[];
	/**
	 * Placeholder text for when the editor is empty.
	 **/
	placeholder?: string;
	/**
	 * Override the default editor wrapper class. Does not override internal classes.
	 **/
	className?: string;
	/**
	 * An array of hashtags to be used in the editor. If provided, the editor will automatically
	 * enable hashtag functionality including hashtag suggestions and hashtag highlighting.
	 * @default []
	 * */
	hashTags?: string[];
	/**
	 * The namespace to use for the editor. This is used to prevent collisions with other editors
	 * on the page. If you have multiple editors on the same page, you must provide a unique namespace
	 * for each editor.
	 * @default lexical-editor
	 * */
	namespace?: string;
	/**
	 * If true, the editor will be read only. This means that the user will not be able to edit the
	 * content of the editor, and the styling of the editor will be changed to reflect this.
	 * @default false
	 * */
	readOnly?: boolean;
	/**
	 * Maximum length for text in the editor. Adds a class `editor-chatacter-limit` to any text that
	 * exceeds the limit.
	 */
	maxLength?: number;
	/**
	 * minimum height for the element.
	 */
	minHeight?: number | null;
	/**
	 * Displays a character counter below the editor.
	 * @default false
	 */
	showCharacterCounter?: boolean;
	/**
	 * The id of for the component.
	 */
	id?: string;
};

/**
 * @deprecated Use the TextEditor component from the py-components package instead.
 */
export const TextEditor = ({
	label,
	labelProps = {
		size: "caption",
		weight: "regular",
	},
	initialValue,
	isRichText = false,
	disableFeatures,
	placeholder,
	hashTags,
	className,
	namespace,
	readOnly,
	maxLength,
	minHeight = null,
	showCharacterCounter = false,
	autoFocus = false,
	onChange = () => {},
	id,
}: TextEditorProps) => {
	const handleChange = (editorState: EditorState) => {
		editorState.read(() => {
			const markdown = $convertToMarkdownString(TRANSFORMERS);
			const plainText = editorState._nodeMap?.get("root")?.__cachedText;
			onChange(markdown, plainText);
		});
	};

	const nodes = useMemo(() => {
		return getEditorNodes(disableFeatures);
	}, [disableFeatures]);

	const plugins = useMemo(() => {
		return getEditorPlugins(
			isRichText,
			handleChange,
			disableFeatures,
			placeholder,
			hashTags,
			readOnly,
			maxLength,
			autoFocus
		);
	}, [isRichText, handleChange, disableFeatures, placeholder, hashTags, autoFocus]);

	const editorParagraph = document.getElementsByClassName(css.editorParagraph);

	const [editorHeight, setEditorHeight] = useState(minHeight);

	// set the default height of the text area according to the height of the content
	useEffect(() => {
		if (editorParagraph.length > 0 && minHeight === null) {
			let height = 0;
			// for each paragraph in the editor, get the height of the paragraph, including the margin
			for (let i = 0; i < editorParagraph.length; i++) {
				const style = window.getComputedStyle(editorParagraph[i]);
				height += editorParagraph[i].clientHeight + parseInt(style.marginTop) + parseInt(style.marginBottom);
			}
			setEditorHeight(height);
		}
	}, [editorParagraph]);

	const CharacterCounter = () => {
		if (!showCharacterCounter || !maxLength) return;

		return <CharacterCounterPlugin maxLength={maxLength} />;
	};

	return (
		<div>
			{label ? (
				<>
					<Text element="label" htmlFor={id} {...labelProps}>
						{label}
					</Text>
					<Spacer height={1} />
				</>
			) : null}
			<div
				id={id}
				className={`${readOnly ? css.editorWrapperReadonly : css.editorWrapper} ${className ?? ""}`}
				style={editorHeight ? { minHeight: editorHeight } : undefined}
			>
				<LexicalComposer
					key={initialValue}
					initialConfig={{
						namespace: namespace || DEFAULT_EDITOR_NAMESPACE,
						nodes: nodes,
						editorState: () =>
							initialValue ? $convertFromMarkdownString(initialValue ?? "", TRANSFORMERS) : emptyState(),
						theme,
						onError: () => {},
						editable: !readOnly,
					}}
				>
					<div className={css.editorFull}>
						<div className={css.editorContainer}>
							<div
								className={css.editorInner}
								style={editorHeight ? { minHeight: editorHeight - 10 } : undefined}
								data-tesetid="editorInner"
							>
								{plugins}
							</div>
							<CharacterCounter />
						</div>
					</div>
				</LexicalComposer>
			</div>
		</div>
	);
};
